// Administration › Calendar in words. The server sends facts (official observances with their source, this business's
// closure days); this file only turns them into what a person reads. It decides no pay and no permission.

import { addDays, fullDate, shortDate } from "../../../components/calendar";

export const DAY_CLASS_WORD = {
  REGULAR_HOLIDAY: "Regular holiday",
  SPECIAL_NON_WORKING: "Special non-working day",
  SPECIAL_WORKING: "Special working day",
};
const NON_WORKING = new Set(["REGULAR_HOLIDAY", "SPECIAL_NON_WORKING"]);
export const isNonWorking = (dayClass) => NON_WORKING.has(dayClass);
export const dayClassWord = (dayClass) => DAY_CLASS_WORD[dayClass] || "Official day";

export const CLOSURE_KIND = "Closed · your business";
export const CHECKED_BY_HAND = "Checked by hand in payroll";

// One short phrase per kind of day, for the list. The full explanation is in the day's detail.
const PAY_PHRASE = {
  REGULAR_HOLIDAY: "Paid holiday · double pay if worked",
  SPECIAL_NON_WORKING: "No work, no pay · 30% more if worked",
  SPECIAL_WORKING: "Ordinary working day",
};

const WHAT_PAYROLL_DOES = {
  REGULAR_HOLIDAY: "Not worked: salaried pay is unchanged, and daily-paid employees get the day's wage if they worked or were on paid leave the working day before. Worked: double pay for the first 8 hours, more on a rest day.",
  SPECIAL_NON_WORKING: "Not worked: salaried pay is unchanged, and daily- and hourly-paid employees are not paid for the day (no work, no pay). Worked: 30% more for the first 8 hours, 50% more on a rest day.",
  SPECIAL_WORKING: "Treated as an ordinary working day: ordinary pay, rest-day and overtime rules apply, and an absence counts as an absence. No holiday pay is added.",
};
const WHAT_A_PERSON_CHECKS = "Overtime on this day, employees whose holiday pay is set to be reviewed or isn't set, and anything your business grants on top are checked by a person in payroll.";
export const MAINTAINED_BY = "Maintained by Apparify from published government declarations. Your business cannot change official holidays. This explains how Apparify handles the day; it is not legal advice.";

/** "Proclamation No. 1189, s. 2026" */
export function sourceName(source) {
  if (!source) return "";
  const type = String(source.issuanceType || "").toLowerCase() === "proclamation" ? "Proclamation" : "Issuance";
  return `${type} No. ${source.number}, s. ${source.seriesYear}`;
}

/** Only real web links are offered as links. */
const safeUrl = (url) => (typeof url === "string" && /^https:\/\//i.test(url) ? url : null);

/**
 * The year as agenda entries. Several official observances on one date stay separate entries (no legal winner is
 * chosen) and each says payroll is checked by hand.
 */
export function calendarEntries(year, { show = "all" } = {}) {
  if (!year) return [];
  const today = year.today || "";
  const perDate = new Map();
  for (const day of year.officialDays || []) perDate.set(day.date, (perDate.get(day.date) || 0) + 1);
  const baseKey = year.baseSource?.key || null;
  const entries = [];
  if (show !== "closures") {
    for (const day of year.officialDays || []) {
      const several = perDate.get(day.date) > 1;
      const announcedLater = baseKey && day.source?.key !== baseKey && day.declaredOn && today && day.declaredOn >= addDays(today, -30);
      entries.push({
        key: `official-${day.id}`,
        type: "official",
        date: day.date,
        title: day.name,
        kind: dayClassWord(day.dayClass),
        meta: several || day.payrollSupport === "REVIEW" ? CHECKED_BY_HAND : PAY_PHRASE[day.dayClass] || "",
        flag: announcedLater ? "New" : "",
        past: Boolean(today) && day.date < today,
        official: day,
      });
    }
  }
  if (show !== "official") {
    for (const closure of year.closures || []) {
      entries.push({
        key: `closure-${closure.id}`,
        type: "closure",
        date: closure.date,
        title: closure.name,
        kind: CLOSURE_KIND,
        meta: closure.note || "Not counted as an absence · no holiday pay",
        marked: true,
        past: Boolean(today) && closure.date < today,
        closure,
      });
    }
  }
  return entries;
}

/** Everything official on a date, for the detail of an official entry. */
export function officialDetail(year, date) {
  const days = (year?.officialDays || []).filter((day) => day.date === date);
  if (!days.length) return null;
  const several = days.length > 1;
  const baseKey = year?.baseSource?.key || null;
  const first = days[0];
  const facts = (day) => {
    const primary = safeUrl(day.source?.primaryReference?.url);
    const supporting = safeUrl(day.source?.corroboration?.url);
    return [
      ...(several ? [{ label: "Official holiday", value: day.name }] : []),
      { label: "Kind", value: dayClassWord(day.dayClass) },
      { label: "Applies to", value: "The whole country" },
      { label: "Declared by", value: `${sourceName(day.source)}${day.source?.signedOn ? ` · signed ${shortDate(day.source.signedOn)}` : ""}` },
      ...(baseKey && day.source?.key !== baseKey && day.declaredOn ? [{ label: "Announced", value: shortDate(day.declaredOn) }] : []),
      ...(day.source?.issuingAuthority ? [{ label: "Issued by", value: day.source.issuingAuthority }] : []),
      ...(primary ? [{ label: "Official source", link: { href: primary, text: day.source.primaryReference.label || "View the official source" } }] : []),
      ...(supporting ? [{ label: "Supporting source", link: { href: supporting, text: day.source.corroboration.label || "View the supporting source" } }] : []),
    ];
  };
  return {
    title: several ? days.map((day) => day.name).join(" and ") : first.name,
    subtitle: `${fullDate(date)}${several ? "" : ` · ${dayClassWord(first.dayClass)}`}`,
    several,
    groups: days.map((day) => ({ key: day.id, name: day.name, facts: facts(day) })),
    payroll: several
      ? "More than one official holiday falls on this date. Apparify does not choose between them: payroll for this date is checked by a person, and the amount is entered once."
      : first.payrollSupport === "REVIEW"
        ? "Payroll for this day is checked by a person."
        : WHAT_PAYROLL_DOES[first.dayClass] || "",
    limits: !several && first.dayClass === "SPECIAL_WORKING" ? "" : WHAT_A_PERSON_CHECKS,
  };
}

/** What closing a date means, shown under the Date field before saving. `officialDays` are the observances on it. */
export function closureConsequence(officialDays = []) {
  const nonWorking = officialDays.filter((day) => isNonWorking(day.dayClass));
  if (nonWorking.length) {
    return {
      tone: "warning",
      blocked: true,
      text: `This date is already an official holiday (${nonWorking.map((day) => day.name).join(", ")}). It does not need a closure day.`,
    };
  }
  if (officialDays.some((day) => day.dayClass === "SPECIAL_WORKING")) {
    return {
      tone: "neutral",
      blocked: false,
      text: "This is an official special working day. Closing is your business decision; the closure adds no holiday premium.",
    };
  }
  return { tone: "neutral", blocked: false, text: CLOSURE_ORDINARY };
}

export const CLOSURE_ORDINARY = "Scheduled employees will not be marked absent and the day won't use their leave. Salaried pay follows existing payroll rules. Pay for daily- and hourly-paid employees who don't work that day is checked in payroll.";

export const CLOSURE_LIMITS = { nameMin: 2, nameMax: 80, noteMax: 300 };

/** Client-side checks that mirror the server's (the server stays the authority). Returns `{ field: message }`. */
export function closureFormErrors(form, { today, lastDate } = {}) {
  const errors = {};
  const name = String(form.name || "").trim();
  if (!form.date) errors.date = "Choose the date.";
  else if (today && form.date < today) errors.date = "Choose today or a later date.";
  else if (lastDate && form.date > lastDate) errors.date = `Choose a date up to ${shortDate(lastDate)}. Later official calendars are not in Apparify yet.`;
  if (name.length < CLOSURE_LIMITS.nameMin) errors.name = "Give the closure day a name of at least 2 characters.";
  else if (name.length > CLOSURE_LIMITS.nameMax) errors.name = `Keep the name to ${CLOSURE_LIMITS.nameMax} characters.`;
  if (String(form.note || "").trim().length > CLOSURE_LIMITS.noteMax) errors.note = `Keep the note to ${CLOSURE_LIMITS.noteMax} characters.`;
  return errors;
}
